import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  hireOrders, staffTransfers, dismissalOrders, civilLawContracts,
  plannedAccrualOrders,
} from '@/api/hr';
import TchTable from '@/shared/components/TchTable';

/**
 * Секція «Документи працівника» на картці (як у 1С).
 *
 * Два блоки:
 *  1. Кадрові документи — спільний таймлайн (прийом + переміщення + звільнення),
 *     відсортований за датою події. Клік → редактор документа у тому ж вікні.
 *  2. Договори ЦПУ — окремий список.
 *
 * Посада/підрозділ резолвляться через posMap/depMap (батьківський редактор уже
 * вантажить ці довідники для «Поточного стану»/«Кадрової історії»).
 */

interface Props {
  employeeId: string;
  posMap: Map<string, string>;
  depMap: Map<string, string>;
  /** Які блоки показати: 'all' (обидва), 'kadr' (лише кадрові накази),
   *  'civil' (лише договори ЦПУ). Для вкладок картки працівника. */
  show?: 'all' | 'kadr' | 'civil';
  /** Якщо задано — біля заголовка «Договори ЦПУ» показуємо «створити ↗». */
  onCreateCivil?: () => void;
}

type KadrKind = 'hire' | 'transfer' | 'dismissal' | 'accrual';

interface KadrRow {
  id: string;
  kind: KadrKind;
  route: string;          // куди навігувати по кліку
  eventDate: string;      // дата події (для сортування + показу)
  number: string;
  positionId: string | null;
  departmentId: string | null;
  amount: string | null;  // оклад / новий оклад / компенсація
  // Готовий рядок суми (для наказа зміни нарахувань — % / тариф / сума за способом).
  // Якщо заданий — показуємо as-is; інакше форматуємо amount як грошову суму.
  displayAmount?: string;
  extra: string | null;   // причина / стаття КЗпП
  posted: boolean;
  cancelled: boolean;
}

const KIND_LABEL: Record<KadrKind, string> = {
  hire: 'Прийом',
  transfer: 'Переміщення',
  dismissal: 'Звільнення',
  accrual: 'Зміна нарахувань',
};

const KIND_COLOR: Record<KadrKind, string> = {
  hire: '#16a34a',
  transfer: '#2563eb',
  dismissal: '#dc2626',
  accrual: '#9333ea',
};

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('uk-UA') : '—';

const fmtAmount = (v: string | null) =>
  v == null ? '' : `${Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ₴`;

// Значення планового рядка за способом: сума → «X ₴», відсоток → «X%», тариф → «X (тариф)».
const fmtAccrualValue = (kind: string | null | undefined, value: string | null | undefined) => {
  if (value == null) return '';
  const n = Number(value);
  if (kind === 'PERCENT') return `${n}%`;
  if (kind === 'RATE') return `${n} (тариф)`;
  return fmtAmount(value);
};

export default function EmployeeDocumentsSection({
  employeeId, posMap, depMap, show = 'all', onCreateCivil,
}: Props) {
  const navigate = useNavigate();
  const showKadr = show === 'all' || show === 'kadr';
  const showCivil = show === 'all' || show === 'civil';

  // Накази — по employee_id (кожна employee-запис = окреме трудове правовідношення
  // зі своїм табельним; наказ належить своїй записі). refetchOnMount: користувач
  // створює документ через тулбар і повертається — список має бути свіжим.
  const hireQ = useQuery({
    queryKey: ['emp-docs', 'hire', employeeId],
    queryFn: () => hireOrders.list({ employee_id: employeeId, limit: 200 }),
    refetchOnMount: true,
  });
  const xferQ = useQuery({
    queryKey: ['emp-docs', 'transfer', employeeId],
    queryFn: () => staffTransfers.list({ employee_id: employeeId, limit: 200 }),
    refetchOnMount: true,
  });
  const disQ = useQuery({
    queryKey: ['emp-docs', 'dismissal', employeeId],
    queryFn: () => dismissalOrders.list({ employee_id: employeeId, limit: 200 }),
    refetchOnMount: true,
  });
  const civilQ = useQuery({
    queryKey: ['emp-docs', 'civil', employeeId],
    queryFn: () => civilLawContracts.list({ employee_id: employeeId, limit: 200 }),
    refetchOnMount: true,
  });
  // Накази про зміну нарахувань — по employee_id (бекенд фільтрує по рядках наказа,
  // тож масовий наказ потрапляє у картку кожного зі своїх працівників).
  const accrQ = useQuery({
    queryKey: ['emp-docs', 'accrual', employeeId],
    queryFn: () => plannedAccrualOrders.list({ employee_id: employeeId, limit: 200 }),
    refetchOnMount: true,
  });

  const loading = hireQ.isLoading || xferQ.isLoading || disQ.isLoading || accrQ.isLoading;

  // Об'єднаний таймлайн кадрових документів.
  const kadrRows: KadrRow[] = [
    ...(hireQ.data?.items ?? []).map<KadrRow>((d) => ({
      id: d.id, kind: 'hire', route: `/hire-orders/${d.id}`,
      eventDate: d.hire_date, number: d.document_number,
      positionId: d.position_id, departmentId: d.department_id,
      amount: d.salary_amount, extra: null,
      posted: d.posted, cancelled: false,
    })),
    ...(xferQ.data?.items ?? []).map<KadrRow>((d) => ({
      id: d.id, kind: 'transfer', route: `/staff-transfers/${d.id}`,
      eventDate: d.transfer_date, number: d.document_number,
      positionId: d.new_position_id, departmentId: d.new_department_id,
      amount: d.new_salary_amount, extra: d.reason,
      posted: d.posted, cancelled: false,
    })),
    ...(disQ.data?.items ?? []).map<KadrRow>((d) => ({
      id: d.id, kind: 'dismissal', route: `/dismissal-orders/${d.id}`,
      eventDate: d.dismiss_date, number: d.document_number,
      positionId: null, departmentId: null,
      amount: d.compensation_amount, extra: d.reason_article,
      posted: d.posted, cancelled: d.state === 'CANCELLED',
    })),
    ...(accrQ.data?.items ?? []).map<KadrRow>((d) => {
      // Значення рядка ЦЬОГО працівника (будь-який спосіб: сума/відсоток/тариф).
      // Беремо перший ADD/CHANGE; формат — за способом. CLOSE-рядки → без суми.
      const empItem = d.items.find(
        (it) => it.employee_id === employeeId && it.action !== 'CLOSE',
      );
      return {
        id: d.id, kind: 'accrual',
        route: `/payroll/planned-accrual-orders/${d.id}`,
        eventDate: d.effective_date, number: d.document_number,
        positionId: null, departmentId: null,
        amount: null,
        displayAmount: empItem ? fmtAccrualValue(empItem.value_kind, empItem.value) : '',
        extra: null,
        posted: d.posted, cancelled: false,
      };
    }),
  ].sort((a, b) => (a.eventDate < b.eventDate ? -1 : a.eventDate > b.eventDate ? 1 : 0));

  const civilRows = civilQ.data?.items ?? [];

  const statusBadge = (posted: boolean, cancelled: boolean) => {
    if (cancelled) return <span style={{ color: '#dc2626' }}>скасовано</span>;
    if (posted) return <span style={{ color: '#16a34a' }}>✓ проведено</span>;
    return <span style={{ color: '#94a3b8' }}>чернетка</span>;
  };

  const rowHover = {
    cursor: 'pointer',
  } as const;

  return (
    <>
      {showKadr && <>
      <h3 style={{ marginTop: '1rem' }}>Кадрові документи</h3>
      {loading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Завантаження…</p>
      ) : kadrRows.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>
          Немає кадрових документів. Оформіть «Прийом на роботу» у тулбарі вище.
        </p>
      ) : (
        <TchTable>
          <thead>
            <tr>
              <th>Дата</th>
              <th>Тип</th>
              <th>№</th>
              <th>Посада</th>
              <th>Підрозділ</th>
              <th style={{ textAlign: 'right' }}>Сума</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {kadrRows.map((r) => (
              <tr key={`${r.kind}-${r.id}`}
                  style={rowHover}
                  title="Відкрити документ"
                  onClick={() => navigate(r.route)}>
                <td>{fmtDate(r.eventDate)}</td>
                <td style={{ fontWeight: 600, color: KIND_COLOR[r.kind] }}>
                  {KIND_LABEL[r.kind]}
                </td>
                <td>{r.number}</td>
                <td>
                  {r.positionId ? posMap.get(r.positionId) ?? '—' : '—'}
                </td>
                <td>
                  {r.departmentId ? depMap.get(r.departmentId) ?? '—' : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {r.displayAmount ?? fmtAmount(r.amount)}
                </td>
                <td>{statusBadge(r.posted, r.cancelled)}</td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}
      </>}

      {showCivil && <>
      <h3 style={{ marginTop: '1rem', display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
        Договори ЦПУ
        {onCreateCivil && (
          <button type="button" onClick={onCreateCivil}
            style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              fontSize: '0.82rem', fontWeight: 'normal', color: 'var(--primary)',
            }}>
            створити ↗
          </button>
        )}
      </h3>
      {civilQ.isLoading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Завантаження…</p>
      ) : civilRows.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>Немає договорів ЦПУ.</p>
      ) : (
        <TchTable>
          <thead>
            <tr>
              <th>Дата</th>
              <th>№</th>
              <th>Предмет</th>
              <th>Період</th>
              <th style={{ textAlign: 'right' }}>Сума</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {civilRows.map((c) => (
              <tr key={c.id}
                  style={rowHover}
                  title="Відкрити договір"
                  onClick={() => navigate(`/civil-law-contracts/${c.id}`)}>
                <td>{fmtDate(c.document_date)}</td>
                <td><a style={{ color: 'var(--primary)', cursor: 'pointer' }}>{c.document_number}</a></td>
                <td>{c.contract_subject ?? '—'}</td>
                <td>
                  {fmtDate(c.date_from)}{c.date_to ? ` — ${fmtDate(c.date_to)}` : ''}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtAmount(c.total_amount)}</td>
                <td>
                  {statusBadge(c.posted, c.state === 'CANCELLED')}
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}
      </>}
    </>
  );
}
